import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Id, LiveStatus } from '@/types/api';

export type BaseLayer = 'street' | 'muted' | 'satellite';
export type VehicleSort = 'status' | 'plate' | 'updated' | 'speed';

interface UiState {
  sidebarCollapsed: boolean;
  drawerOpen: boolean;
  selectedVehicleId: Id | null;
  /** Set when a row click should recentre the map; cleared once the map obeys. */
  flyToVehicleId: Id | null;
  baseLayer: BaseLayer;
  showGeofences: boolean;
  showTrails: boolean;
  statusFilter: LiveStatus[];
  groupFilter: string | null;
  search: string;
  sort: VehicleSort;
  alertSound: boolean;

  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setDrawerOpen: (open: boolean) => void;
  toggleDrawer: () => void;
  selectVehicle: (id: Id | null, options?: { fly?: boolean }) => void;
  consumeFlyTo: () => void;
  setBaseLayer: (layer: BaseLayer) => void;
  setShowGeofences: (show: boolean) => void;
  setShowTrails: (show: boolean) => void;
  toggleStatusFilter: (status: LiveStatus) => void;
  clearFilters: () => void;
  setGroupFilter: (group: string | null) => void;
  setSearch: (value: string) => void;
  setSort: (sort: VehicleSort) => void;
  setAlertSound: (on: boolean) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      drawerOpen: true,
      selectedVehicleId: null,
      flyToVehicleId: null,
      baseLayer: 'street',
      showGeofences: true,
      showTrails: false,
      statusFilter: [],
      groupFilter: null,
      search: '',
      sort: 'status',
      alertSound: false,

      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      setDrawerOpen: (drawerOpen) => set({ drawerOpen }),
      toggleDrawer: () => set((s) => ({ drawerOpen: !s.drawerOpen })),
      selectVehicle: (id, options) =>
        set({ selectedVehicleId: id, flyToVehicleId: options?.fly ? id : null }),
      consumeFlyTo: () => set({ flyToVehicleId: null }),
      setBaseLayer: (baseLayer) => set({ baseLayer }),
      setShowGeofences: (showGeofences) => set({ showGeofences }),
      setShowTrails: (showTrails) => set({ showTrails }),
      toggleStatusFilter: (status) =>
        set((s) => ({
          statusFilter: s.statusFilter.includes(status)
            ? s.statusFilter.filter((v) => v !== status)
            : [...s.statusFilter, status],
        })),
      clearFilters: () => set({ statusFilter: [], groupFilter: null, search: '' }),
      setGroupFilter: (groupFilter) => set({ groupFilter }),
      setSearch: (search) => set({ search }),
      setSort: (sort) => set({ sort }),
      setAlertSound: (alertSound) => set({ alertSound }),
    }),
    {
      name: 'olefleet.ui',
      // Selection and transient filters are per-session; layout choices persist.
      partialize: (s) => ({
        sidebarCollapsed: s.sidebarCollapsed,
        drawerOpen: s.drawerOpen,
        baseLayer: s.baseLayer,
        showGeofences: s.showGeofences,
        showTrails: s.showTrails,
        sort: s.sort,
        alertSound: s.alertSound,
      }),
    },
  ),
);
